import React, { useState } from 'react';
import { ExternalLink, Star, Download, Clock, User, Code, Tag, CheckCircle, Brain, Zap, Eye, Rocket, Share2 } from 'lucide-react';
import { Agent } from '../lib/supabase';
import { AgentAnalysisModal } from './AgentAnalysisModal';
import { AgentUIModal } from './AgentUIModal';
import { DeploymentWorkflow } from './DeploymentWorkflow';
import { AgentDownloadModal } from './AgentDownloadModal';
import { useAgentInteractions } from '../hooks/useAgentInteractions';
import { RealAgentInterface } from './RealAgentInterface';

interface AgentCardProps {
  agent: Agent;
  onShareAgent?: () => void;
}

export function AgentCard({ agent, onShareAgent }: AgentCardProps) {
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [showUI, setShowUI] = useState(false);
  const [showDeployment, setShowDeployment] = useState(false);
  const [showDownload, setShowDownload] = useState(false);
  const [showRealInterface, setShowRealInterface] = useState(false);
  const { trackInteraction } = useAgentInteractions();

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const getSourceColor = (source: string) => {
    const colors: Record<string, string> = {
      github: 'bg-gray-900 text-white',
      huggingface: 'bg-yellow-500 text-white',
      google: 'bg-blue-600 text-white',
      microsoft: 'bg-blue-500 text-white',
      langchain: 'bg-green-600 text-white',
      other: 'bg-purple-600 text-white'
    };
    return colors[source] || colors.other;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Unknown';
    }
  };

  const handleViewAgent = () => {
    trackInteraction(agent.id, 'view');
    window.open(agent.source_url, '_blank');
  };

  const handleAnalyze = () => {
    trackInteraction(agent.id, 'analyze');
    setShowAnalysis(true);
  };

  const handleGenerateUI = () => {
    trackInteraction(agent.id, 'generate_ui');
    setShowUI(true);
  };

  const handleDeploy = () => {
    trackInteraction(agent.id, 'view', { action: 'deploy' });
    setShowDeployment(true);
  };

  const handleDownload = () => {
    trackInteraction(agent.id, 'view', { action: 'download' });
    setShowDownload(true);
  };

  const handleShare = () => {
    if (onShareAgent) {
      trackInteraction(agent.id, 'view', { action: 'share' });
      onShareAgent();
    }
  };

  const handleOpenRealInterface = () => {
    trackInteraction(agent.id, 'use_ui');
    setShowRealInterface(true);
  };

  const isRealAgent = agent.metadata?.is_real;
  return (
    <>
      <div className="group card-modern p-4 md:p-6 hover:shadow-strong hover:border-primary-300 hover:-translate-y-1 transition-all duration-300">
        {/* Header */}
        <div className="flex items-start justify-between mb-3 md:mb-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 md:mb-2">
              <h3 className="text-base md:text-lg font-semibold text-dark-900 group-hover:text-primary-600 transition-colors truncate">
                {agent.name}
              </h3>
              <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
                <span className={`px-1.5 py-0.5 md:px-2 md:py-1 rounded-lg text-xs font-medium ${getSourceColor(agent.source)}`}>
                  {agent.source}
                </span>
                {agent.is_verified && (
                  <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-green-500" title="Verified Agent" />
                )}
              </div>
            </div>
            <p className="text-xs md:text-sm text-dark-600 line-clamp-2 mb-2 md:mb-3 leading-relaxed">
              {agent.description || 'No description available'}
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-3 md:gap-4 mb-3 md:mb-4 text-xs md:text-sm text-dark-500">
          {agent.stars > 0 && (
            <div className="flex items-center gap-1">
              <Star className="w-3 h-3 md:w-4 md:h-4" />
              <span>{formatNumber(agent.stars)}</span>
            </div>
          )}
          {agent.downloads > 0 && (
            <div className="flex items-center gap-1">
              <Download className="w-3 h-3 md:w-4 md:h-4" />
              <span>{formatNumber(agent.downloads)}</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3 md:w-4 md:h-4" />
            <span>{formatDate(agent.last_updated)}</span>
          </div>
        </div>

        {/* Metadata */}
        <div className="space-y-1 md:space-y-2 mb-3 md:mb-4">
          {agent.author && (
            <div className="flex items-center gap-1 md:gap-2 text-xs md:text-sm text-dark-600">
              <User className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" />
              <span className="truncate">{agent.author}</span>
            </div>
          )}
          {agent.language && (
            <div className="flex items-center gap-1 md:gap-2 text-xs md:text-sm text-dark-600">
              <Code className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" />
              <span>{agent.language}</span>
            </div>
          )}
          {agent.category && (
            <div className="flex items-center gap-1 md:gap-2 text-xs md:text-sm text-dark-600">
              <Tag className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" />
              <span>{agent.category}</span>
            </div>
          )}
        </div>

        {/* Tags */}
        {agent.tags && agent.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3 md:mb-4">
            {agent.tags.slice(0, 3).map((tag, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-primary-50 text-primary-700 rounded-lg text-xs font-medium border border-primary-200"
              >
                {tag}
              </span>
            ))}
            {agent.tags.length > 3 && (
              <span className="px-2 py-1 bg-dark-50 text-dark-500 rounded-lg text-xs font-medium">
                +{agent.tags.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="grid grid-cols-3 gap-1 md:gap-2">
          {isRealAgent ? (
            <button
              onClick={handleOpenRealInterface}
              className="flex items-center justify-center gap-1 px-2 py-2 md:px-3 md:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 hover:shadow-medium transition-all duration-200 text-xs md:text-sm font-medium"
            >
              <Bot className="w-3 h-3 md:w-4 md:h-4" />
              <span className="hidden xs:inline">Chat</span>
            </button>
          ) : (
            <button
              onClick={handleViewAgent}
              className="flex items-center justify-center gap-1 px-2 py-2 md:px-3 md:py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 hover:shadow-medium transition-all duration-200 text-xs md:text-sm font-medium"
            >
              <ExternalLink className="w-3 h-3 md:w-4 md:h-4" />
              <span className="hidden xs:inline">View</span>
            </button>
          )}
          <button
            onClick={handleDownload}
            className="flex items-center justify-center gap-1 px-2 py-2 md:px-3 md:py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 hover:shadow-medium transition-all duration-200 text-xs md:text-sm font-medium"
          >
            <Download className="w-3 h-3 md:w-4 md:h-4" />
            <span className="hidden xs:inline">Download</span>
          </button>
          <button
            onClick={handleShare}
            className="flex items-center justify-center gap-1 px-2 py-2 md:px-3 md:py-2 bg-dark-700 text-white rounded-lg hover:bg-dark-800 hover:shadow-medium transition-all duration-200 text-xs md:text-sm font-medium"
          >
            <Share2 className="w-3 h-3 md:w-4 md:h-4" />
            <span className="hidden xs:inline">Share</span>
          </button>
          <button
            onClick={handleAnalyze}
            className="flex items-center justify-center gap-1 px-2 py-2 md:px-3 md:py-2 border-2 border-dark-200 text-dark-700 rounded-lg hover:bg-dark-50 hover:border-primary-300 transition-all duration-200 text-xs md:text-sm font-medium"
          >
            <Brain className="w-3 h-3 md:w-4 md:h-4" />
            <span className="hidden xs:inline">Analyze</span>
          </button>
          <button
            onClick={handleGenerateUI}
            className="flex items-center justify-center gap-1 px-2 py-2 md:px-3 md:py-2 border-2 border-dark-200 text-dark-700 rounded-lg hover:bg-dark-50 hover:border-primary-300 transition-all duration-200 text-xs md:text-sm font-medium"
            title="Generate Interactive UI"
          >
            <Zap className="w-3 h-3 md:w-4 md:h-4" />
            <span className="hidden xs:inline">UI</span>
          </button>
          <button
            onClick={handleDeploy}
            className="flex items-center justify-center gap-1 px-2 py-2 md:px-3 md:py-2 bg-gradient-to-r from-primary-600 to-accent-600 text-white rounded-lg hover:from-primary-700 hover:to-accent-700 hover:shadow-medium transition-all duration-200 text-xs md:text-sm font-medium"
          >
            <Rocket className="w-3 h-3 md:w-4 md:h-4" />
            <span className="hidden xs:inline">Deploy</span>
          </button>
        </div>
      </div>

      {/* Analysis Modal */}
      <AgentAnalysisModal
        agent={agent}
        isOpen={showAnalysis}
        onClose={() => setShowAnalysis(false)}
      />

      {/* UI Generation Modal */}
      <AgentUIModal
        agent={agent}
        isOpen={showUI}
        onClose={() => setShowUI(false)}
      />

      {/* Deployment Workflow Modal */}
      <DeploymentWorkflow
        agent={agent}
        isOpen={showDeployment}
        onClose={() => setShowDeployment(false)}
      />

      {/* Download Modal */}
      <AgentDownloadModal
        agent={agent}
        isOpen={showDownload}
        onClose={() => setShowDownload(false)}
      />

      {/* Real Agent Interface */}
      {isRealAgent && (
        <RealAgentInterface
          agent={agent}
          isOpen={showRealInterface}
          onClose={() => setShowRealInterface(false)}
        />
      )}
    </>
  );
}